import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react'

import { cn } from '../cn'

// IconButton — compact icon-only action (design/components/button.html §1.5).
// ALWAYS requires aria-label (it has no visible text); the icon is decorative.

const iconButtonVariants = cva(
  [
    'inline-flex cursor-pointer items-center justify-center',
    'transition-[background-color,color] duration-quick ease-vysto',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue focus-visible:outline-offset-2',
    'disabled:cursor-not-allowed disabled:text-text-3',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        ghost: 'bg-transparent text-text-2 hover:bg-hover hover:text-text',
        secondary:
          'border border-input-line bg-surface text-text-2 hover:bg-hover hover:text-text disabled:bg-surface',
      },
      size: {
        sm: 'size-7 rounded-[7px] [&_svg]:size-[13px]',
        md: 'size-[34px] rounded-[8px] [&_svg]:size-[15px]',
        lg: 'size-10 rounded-md [&_svg]:size-[17px]',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  },
)

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  icon: ReactNode
  /** Required — IconButton has no visible text, so it needs an accessible name. */
  'aria-label': string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant, size, icon, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...rest}
    >
      {icon}
    </button>
  )
})
IconButton.displayName = 'IconButton'
